import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import debounce from 'lodash/debounce';
import { unByKey } from 'ol/Observable';
import { getPointResolution } from 'ol/proj';
import { CloneMap } from '@tmapy/utils';
import { SvgSpinner, Icon, SIZE } from '@tmapy/style-guide';
import { QUERY_STATE } from '../../constants';
import { LocaleContext } from '../../LocaleContext';
import { setMap } from '../mapStore';
import { Attribution } from '../mapattribution/Attribution';
export class MapPreview extends React.PureComponent {
    DPI = 96;
    printMap;
    thisRef;
    mapRef;
    lastScale = NaN;
    lastCenterStr = '';
    loadEvents = [];
    loadCount = 0;
    observer;
    constructor(props) {
        super(props);
        this.printMap = new CloneMap(this.props.source, this.props.changeScale ? debounce(this.changeResolution, 50) : undefined, this.props.changeCenter ? debounce(this.changeCenter, 50) : undefined, this.props.proj4, this.props.printSourceCallback);
        // Rozmery gridu na klientu a na serveru, kde bezi sluzba pro generovani vystupu, se mohou lisit.
        // Jsou zavisle napriklad na proporcich pisma, ktere je na danem stroji nainstalovane.
        // Tedy element, ktery obsahuje text muze byt ruzne siroky. Proto aby preview presne odpovidalo tomu,
        // co vygneruje sluzba, je nutne rozmery nekterych komponent zafixovat a zafixovane posilat na sluzbu.
        // Zatim se to tyka pouze mapy, ktera bez tohoto workaroundu meritkove neodpovida.
        this.state = {
            containerWidth: undefined,
        };
        // observe transformation of map element for refreshing of map to prevent map deformation
        // https://tmapy.visualstudio.com/DefaultCollection/tmapy/_workitems/edit/22670
        this.observer = new ResizeObserver((entries) => {
            entries.forEach(() => {
                this.printMap.refresh();
            });
        });
        this.thisRef = React.createRef();
        this.mapRef = React.createRef();
    }
    /**
     * Vyvolano z CloneMap na OL udalosti 'change:resolution' a 'change:center'
     * a jednou vyvolano z primo componentDidMount()
     */
    changeCenter = (e) => {
        this.props.changeCenter?.(this.lastScale, e.target.getCenter());
        const center = e.target.getCenter();
        const minPointResolution = getPointResolution(e.target.getProjection(), this.printMap.map.getView().getMinResolution(), center);
        const maxPointResolution = getPointResolution(e.target.getProjection(), this.printMap.map.getView().getMaxResolution(), center);
        const minMaxScale = {
            minScale: Math.round((minPointResolution * this.DPI) / 0.0254),
            maxScale: Math.round((maxPointResolution * this.DPI) / 0.0254),
        };
        this.props.setMinMaxScale(minMaxScale);
    };
    changeResolution = (e) => {
        const viewResolution = e.target.getResolution();
        const center = e.target.getCenter();
        const resolution = getPointResolution(e.target.getProjection(), viewResolution, center);
        const scale = Math.round((resolution * this.DPI) / 0.0254);
        // aby se nespustilo podruhe z componentDidUpdate()->setScale()
        if (this.lastScale !== scale || this.lastCenterStr !== center.toString()) {
            this.lastScale = scale;
            this.lastCenterStr = center.toString();
            this.props.changeScale?.(scale, center);
        }
    };
    setScale = (scale, center) => {
        if (!this.printMap.map)
            return;
        const viewResolution = (scale / this.DPI) * 0.0254;
        const resolution = (viewResolution * viewResolution) /
            getPointResolution(this.printMap.map.getView().getProjection(), viewResolution, this.printMap.map.getView().getCenter());
        this.printMap.setResolution(resolution, center);
    };
    loadStart = () => {
        if (!this.loadCount)
            this.props.changeMapState(QUERY_STATE.WAITING);
        this.loadCount++;
    };
    loadEnd = (e, time = 500) => {
        this.loadCount--;
        if (this.loadCount < 1) {
            this.loadCount = 0;
            setTimeout(() => {
                // pokud se mezitim jeste nezacne loadovat neco jineho
                if (!this.loadCount) {
                    this.props.changeMapState(QUERY_STATE.SUCCESS);
                }
            }, time);
        }
    };
    onRenderedMap = (map) => {
        this.loadCount = 0;
        this.loadEvents = [];
        map.getLayers().forEach((l) => {
            const source = l.getSource();
            if (!source)
                return;
            this.loadEvents = this.loadEvents.concat(source.on(['imageloadstart', 'tileloadstart'], this.loadStart));
            this.loadEvents = this.loadEvents.concat(source.on(['imageloadend', 'tileloadend', 'tileloaderror', 'imageloaderror'], this.loadEnd));
        });
        /// aby se provedla kontrola na spinavy data i u nacachovanou mapu
        this.loadStart();
        this.loadEnd(null, 0);
    };
    componentDidUpdate(prevProps) {
        if (this.props.scale && this.props.scale !== prevProps.scale) {
            this.setScale(this.props.scale);
        }
        if (this.props.isDimensionFixed) {
            if (this.thisRef?.current) {
                this.setState({
                    containerWidth: `${this.thisRef.current.offsetWidth}px`,
                });
                if (this.thisRef.current.style.width) {
                    this.props.onDimensionFixed(this.printMap.map, this.props.format);
                }
            }
        }
        else {
            this.setState({
                containerWidth: undefined,
            });
        }
    }
    componentDidMount() {
        const pixelRatio = (this.props.dpi || 300) / this.DPI, map = this.printMap.clone(this.mapRef.current, pixelRatio);
        setMap(this.props.componentIdent, map);
        if (this.props.scale) {
            this.setScale(this.props.scale, this.props.center);
        }
        else {
            // vyvola prvotni nastaveni pro Scale a Center
            this.changeResolution({ target: map.getView() });
            this.changeCenter({ target: map.getView() });
        }
        this.onRenderedMap(map);
        // start observe map element
        this.thisRef.current && this.observer.observe(this.thisRef.current);
        this.props.onAfterCreate && this.props.onAfterCreate(map);
    }
    componentWillUnmount() {
        unByKey(this.loadEvents);
        this.observer.disconnect();
        this.printMap.destroyMap();
        setMap(this.props.componentIdent, null);
    }
    render() {
        return (_jsx(LocaleContext.Consumer, { children: (context) => (_jsxs("div", { className: 'rpx-map', ref: this.thisRef, style: {
                    width: this.state.containerWidth,
                    // Pokud fixnu i vysku, tak to muze nafouknout stranku
                    // a tim padem vytvorit prazdnou stranku na vic
                    // height: this.state.containerHeight
                }, children: [_jsx("div", { className: 'rpx-map--inner', ref: this.mapRef }), this.props.mapState === QUERY_STATE.WAITING && (_jsx("div", { className: 'rpx-map--spinner', title: context.locale.map.waiting, children: _jsx(Icon, { element: _jsx(SvgSpinner, {}), size: SIZE.MED }) })), this.props.mapAttribution?.showOverMap && !this.props.mapAttribution.isHidden && (_jsx("div", { className: 'rpx-map--attributionContainer', children: _jsx(Attribution, { children: this.props.mapAttribution.content }) }))] })) }));
    }
}
//# sourceMappingURL=preview.js.map